import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    AlertTriangle, RefreshCw, Loader2, ChevronLeft, ChevronRight, Search, X,
    ArrowUp, ArrowDown, ArrowUpDown, Info, Send, Radio, GitBranch,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { getErrorOverview, getErrorsByPage } from '@/services/api/errorAnalytics';
import type { ErrorByApp, ErrorByCode, ErrorCounts, ErrorWindow, ErrorSource } from '@/types/errorAnalytics';

type Tone = 'critical' | 'warn' | 'neutral';

// ─── Rótulo preciso por (código, subcódigo) — usado no "Por código" ─────
function labelFor(code: number | null, subcode: number | null): { key: string; label: string; hint: string; tone: Tone } {
    if (code == null) return { key: 'timeout', label: 'Timeout / desconhecido', hint: 'Rede ou worker sob stress (inclui circuit breaker). Reprocessar.', tone: 'neutral' };
    switch (code) {
        case 190: return { key: '190', label: 'Token / app morto', hint: 'App deletado ou token inválido. Reconectar a página num app vivo.', tone: 'critical' };
        case 2022: return { key: '2022', label: 'Bloqueio por spam', hint: 'App queimado pela Meta. Reduzir volume; trocar tag ACCOUNT_UPDATE por template aprovado.', tone: 'critical' };
        case 551: return { key: '551', label: 'Pessoa indisponível', hint: 'Bloqueou/desativou a página ou fora de alcance. Audiência queimada — limpar a base.', tone: 'neutral' };
        case 613: return { key: '613', label: 'Rate limit', hint: 'Excesso de chamadas no app. Distribuir páginas entre apps diferentes.', tone: 'warn' };
        case 10: return { key: '10', label: 'Fora da janela 24h', hint: 'Promocional bloqueado. Usar template UTILITY aprovado ou enviar em 24h.', tone: 'warn' };
        case 100:
            if (subcode === 2018001) return { key: '100:nouser', label: 'Nenhum usuário correspondente', hint: 'O PSID não bate com nenhum usuário. Lead velho/inválido — limpar.', tone: 'neutral' };
            if (subcode === 2018247) return { key: '100:perm', label: 'Sem permissão de perfil', hint: 'Permissão insuficiente pra acessar o perfil do usuário.', tone: 'warn' };
            return { key: '100:param', label: 'Parâmetro inválido', hint: 'Não pode enviar mensagem pra esse id (destinatário/parâmetro inválido).', tone: 'neutral' };
        default: return { key: String(code), label: `Erro #${code}`, hint: 'Código fora dos principais.', tone: 'neutral' };
    }
}

// Rótulo curto por código (nível de bucket) — usado nos chips de app/página.
const BUCKETS: { key: keyof ErrorCounts; code: number | null; label: string; tone: Tone }[] = [
    { key: 'e190', code: 190, label: 'Token/app morto', tone: 'critical' },
    { key: 'e2022', code: 2022, label: 'Bloqueio spam', tone: 'critical' },
    { key: 'e10', code: 10, label: 'Fora 24h', tone: 'warn' },
    { key: 'e613', code: 613, label: 'Rate limit', tone: 'warn' },
    { key: 'e551', code: 551, label: 'Indisponível', tone: 'neutral' },
    { key: 'e100', code: 100, label: 'Destinatário inválido', tone: 'neutral' },
    { key: 'e_timeout', code: null, label: 'Timeout', tone: 'neutral' },
    { key: 'e_outros', code: null, label: 'Outros', tone: 'neutral' },
];

const WINDOWS: { id: ErrorWindow; label: string }[] = [
    { id: '1h', label: '1h' }, { id: '24h', label: '24h' }, { id: '7d', label: '7 dias' },
];
const SOURCES: { id: ErrorSource; label: string; icon: typeof Send }[] = [
    { id: 'broadcast', label: 'Broadcast', icon: Send },
    { id: 'esteira', label: 'Esteira', icon: Radio },
    { id: 'flow', label: 'Flow', icon: GitBranch },
];
const toneChip: Record<Tone, string> = {
    critical: 'bg-red-50 text-red-700 border-red-200',
    warn: 'bg-amber-50 text-amber-700 border-amber-200',
    neutral: 'bg-gray-100 text-gray-600 border-gray-200',
};
const toneBar: Record<Tone, string> = { critical: 'bg-red-400', warn: 'bg-amber-400', neutral: 'bg-gray-400' };
const fmt = (n: number) => (n || 0).toLocaleString('pt-BR');
const pctColor = (p: number) => (p >= 30 ? 'text-emerald-600' : p >= 5 ? 'text-amber-600' : 'text-red-600');

/** Chips dos buckets de erro não-zerados, do maior. Clicável pra filtrar por código. */
function ErrorMix({ row, max = 5, onPick }: { row: ErrorCounts; max?: number; onPick?: (code: number | null) => void }) {
    const chips = BUCKETS
        .map((b) => ({ ...b, n: (row[b.key] as number) || 0 }))
        .filter((c) => c.n > 0)
        .sort((a, b) => b.n - a.n)
        .slice(0, max);
    if (!chips.length) return <span className="text-gray-300 text-xs">—</span>;
    return (
        <div className="flex flex-wrap gap-1">
            {chips.map((c) => (
                <button
                    key={c.key}
                    onClick={onPick && c.code != null ? (e) => { e.stopPropagation(); onPick(c.code); } : undefined}
                    className={`text-[11px] font-medium px-1.5 py-0.5 rounded border ${toneChip[c.tone]} ${onPick && c.code != null ? 'hover:ring-1 hover:ring-violet-300 cursor-pointer' : 'cursor-default'}`}
                    title={onPick && c.code != null ? `Filtrar por ${c.label}` : c.label}
                >
                    {c.label}: {fmt(c.n)}
                </button>
            ))}
        </div>
    );
}

function StatTile({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
    return (
        <div className="bg-white border rounded-xl px-4 py-3 flex flex-col gap-0.5">
            <span className="text-[11px] uppercase tracking-wide text-gray-500">{label}</span>
            <span className={`text-2xl font-bold tabular-nums leading-tight ${accent || 'text-gray-900'}`}>{value}</span>
            {sub && <span className="text-xs text-gray-500">{sub}</span>}
        </div>
    );
}

type SortDir = 'asc' | 'desc';
interface SortState { col: string; dir: SortDir; }

/** Header de coluna clicável: alterna asc/desc; mostra a seta do estado atual. */
function SortTh({ label, col, sort, onSort, align = 'right' }: {
    label: string; col: string; sort: SortState; onSort: (col: string) => void; align?: 'left' | 'right';
}) {
    const active = sort.col === col;
    return (
        <th
            className={`font-medium px-3 py-2 cursor-pointer select-none hover:text-violet-600 transition-colors ${align === 'right' ? 'text-right' : 'text-left'}`}
            onClick={() => onSort(col)}
            title="Ordenar"
        >
            <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
                {label}
                {active
                    ? (sort.dir === 'asc' ? <ArrowUp className="w-3 h-3 text-violet-600" /> : <ArrowDown className="w-3 h-3 text-violet-600" />)
                    : <ArrowUpDown className="w-3 h-3 opacity-30" />}
            </span>
        </th>
    );
}

export default function ErrorAnalysis() {
    const [win, setWin] = useState<ErrorWindow>('24h');
    const [source, setSource] = useState<ErrorSource>('broadcast');
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [activeOnly, setActiveOnly] = useState(true);
    const [appFilter, setAppFilter] = useState<number | null>(null);
    const [codeFilter, setCodeFilter] = useState<number | 'timeout' | null>(null);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [appSort, setAppSort] = useState<SortState>({ col: 'falha', dir: 'desc' });
    const [pageSort, setPageSort] = useState<SortState>({ col: 'falha', dir: 'desc' });

    const toggleSort = (cur: SortState, col: string): SortState =>
        (cur.col === col ? { col, dir: cur.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'desc' });
    const onAppSort = (col: string) => setAppSort((s) => toggleSort(s, col));
    const onPageSort = (col: string) => { setPageSort((s) => toggleSort(s, col)); setPage(1); };

    // Trocar de fonte zera filtros/ordenação (as fontes têm colunas diferentes).
    const changeSource = (src: ErrorSource) => {
        setSource(src);
        setAppFilter(null); setCodeFilter(null); setSearchInput(''); setSearch(''); setPage(1);
        setPageSort({ col: 'falha', dir: 'desc' });
    };

    // debounce da busca
    useEffect(() => {
        const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 350);
        return () => clearTimeout(t);
    }, [searchInput]);

    const refetchInterval = autoRefresh ? 15000 : false;

    const overview = useQuery({
        queryKey: ['error-overview', win, source],
        queryFn: () => getErrorOverview(win, source),
        refetchInterval,
    });

    const codeParam = codeFilter === 'timeout' ? undefined : codeFilter;
    const byPage = useQuery({
        queryKey: ['error-by-page', win, source, appFilter, codeParam, search, pageSort, page],
        queryFn: () => getErrorsByPage(win, {
            source, app_id: appFilter, error_code: codeParam ?? null, search,
            sort_by: pageSort.col, sort_dir: pageSort.dir, page, per_page: 25,
        }),
        refetchInterval,
    });

    const s = overview.data?.summary;
    const totalPages = byPage.data ? Math.max(1, Math.ceil(byPage.data.total / byPage.data.per_page)) : 1;
    const pageCols = source === 'esteira' ? 5 : 6;

    // Por código: agrupa (code,subcode) por rótulo resolvido, somando totais.
    const codeGroups = useMemo(() => {
        const map = new Map<string, { label: string; hint: string; tone: Tone; code: number | null; total: number }>();
        for (const r of (overview.data?.by_code || []) as ErrorByCode[]) {
            const m = labelFor(r.error_code, r.error_subcode);
            const g = map.get(m.key) || { label: m.label, hint: m.hint, tone: m.tone, code: r.error_code, total: 0 };
            g.total += r.total;
            map.set(m.key, g);
        }
        return Array.from(map.values()).sort((a, b) => b.total - a.total);
    }, [overview.data]);

    // Por app: aplica o toggle "apenas ativos" + ordenação client-side.
    const apps = useMemo(() => {
        let list = (overview.data?.by_app || []) as ErrorByApp[];
        if (activeOnly) list = list.filter((a) => a.app_active !== false);
        const dir = appSort.dir === 'asc' ? 1 : -1;
        return [...list].sort((a, b) => {
            const av = (a[appSort.col as keyof ErrorByApp] as number) ?? 0;
            const bv = (b[appSort.col as keyof ErrorByApp] as number) ?? 0;
            return (av - bv) * dir;
        });
    }, [overview.data, activeOnly, appSort]);

    const appOptions = useMemo(
        () => [{ value: '', label: 'Todos os apps' }, ...apps
            .filter((a) => a.app_id != null)
            .map((a) => ({ value: String(a.app_id), label: `${a.app_name}${a.app_active === false ? ' (inativo)' : ''}` }))],
        [apps],
    );

    const totalFail = s?.falha || 1;
    const hasFilter = appFilter != null || codeFilter != null || !!search;
    const clearFilters = () => { setAppFilter(null); setCodeFilter(null); setSearchInput(''); setSearch(''); setPage(1); };
    const pickCode = (code: number | null) => { setCodeFilter(code == null ? 'timeout' : code); setPage(1); };

    return (
        <div className="p-4 sm:p-6 max-w-[1440px] mx-auto space-y-4">
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-violet-600" />
                        Análise de erros
                    </h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        Envios de broadcast por app e por página. Sucesso = aceito pela API da Meta · Entregue = confirmado por webhook.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="inline-flex bg-gray-100 border rounded-lg p-0.5">
                        {WINDOWS.map((w) => (
                            <button
                                key={w.id}
                                onClick={() => { setWin(w.id); setPage(1); }}
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${win === w.id ? 'bg-white text-violet-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                {w.label}
                            </button>
                        ))}
                    </div>
                    <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer select-none">
                        <input type="checkbox" className="h-4 w-4" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
                        Auto 15s
                    </label>
                    <button onClick={() => { overview.refetch(); byPage.refetch(); }} className="p-2 rounded-lg border hover:bg-gray-50 text-gray-500" title="Atualizar">
                        <RefreshCw className={`w-4 h-4 ${(overview.isFetching || byPage.isFetching) ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Seletor de FONTE de envio (broadcast | esteira | flow) */}
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs uppercase tracking-wide text-gray-400 mr-1">Fonte</span>
                <div className="inline-flex bg-gray-100 border rounded-lg p-0.5">
                    {SOURCES.map((sr) => {
                        const Icon = sr.icon;
                        return (
                            <button
                                key={sr.id}
                                onClick={() => changeSource(sr.id)}
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1.5 ${source === sr.id ? 'bg-white text-violet-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                <Icon className="w-3.5 h-3.5" />
                                {sr.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {overview.isError && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">Não foi possível carregar os erros. Tente atualizar.</div>
            )}

            {/* Resumo */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                {source === 'esteira' ? (
                    <>
                        <StatTile label="Enviados" value={s ? fmt(s.sucesso) : '—'} sub={s ? `${fmt(s.total)} tentativas` : undefined} />
                        <StatTile label="Sucesso" value={s ? `${s.pct_sucesso}%` : '—'} accent={s ? pctColor(s.pct_sucesso) : undefined} />
                        <StatTile label="Falhas" value={s ? fmt(s.falha) : '—'} accent="text-red-600" />
                        <StatTile label="Páginas atingidas" value={s ? fmt(s.pages ?? 0) : '—'} />
                        <StatTile label="Leads no período" value={s ? fmt(s.leads ?? 0) : '—'} />
                    </>
                ) : source === 'flow' ? (
                    <>
                        <StatTile label="Dispatches" value={s ? fmt(s.total) : '—'} sub="mensagens de flow" />
                        <StatTile label="Sucesso" value={s ? `${s.pct_sucesso}%` : '—'} sub={s ? `${fmt(s.sucesso)} enviados` : undefined} accent={s ? pctColor(s.pct_sucesso) : undefined} />
                        <StatTile label="Falhas" value={s ? fmt(s.falha) : '—'} accent="text-red-600" />
                        <StatTile label="Páginas" value={s ? fmt(s.pages ?? 0) : '—'} />
                        <StatTile label="Maior ofensor" value={codeGroups[0]?.label ?? '—'} sub={codeGroups[0] ? `${fmt(codeGroups[0].total)} ocorrências` : undefined} />
                    </>
                ) : (
                    <>
                        <StatTile label="Envios no período" value={s ? fmt(s.total) : '—'} sub={s ? `${fmt(s.em_voo)} em voo` : undefined} />
                        <StatTile label="Sucesso (API)" value={s ? `${s.pct_sucesso}%` : '—'} sub={s ? `${fmt(s.sucesso)} aceitos` : undefined} accent={s ? pctColor(s.pct_sucesso) : undefined} />
                        <StatTile label="Entregues" value={s ? fmt(s.entregues) : '—'} sub={s && s.sucesso ? `${Math.round((s.entregues / s.sucesso) * 100)}% dos aceitos` : undefined} accent="text-emerald-600" />
                        <StatTile label="Falhas" value={s ? fmt(s.falha) : '—'} accent="text-red-600" />
                        <StatTile label="Maior ofensor" value={codeGroups[0]?.label ?? '—'} sub={codeGroups[0] ? `${fmt(codeGroups[0].total)} ocorrências` : undefined} />
                    </>
                )}
            </div>

            {/* Nota do Flow: captura começou no deploy; sem quebra por app. */}
            {source === 'flow' && (
                <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-xl p-3 text-sm flex gap-2">
                    <Info className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>A captura de erro do <b>Flow</b> começou em <b>24/07/2026</b> — janelas anteriores a isso aparecem sem código de erro. Não há quebra <b>por app</b> (o flow não registra o app no envio).</span>
                </div>
            )}

            {/* Por código (broadcast + flow; esteira não tem). Por app só broadcast. */}
            {(source === 'broadcast' || source === 'flow') && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Por código */}
                <section className={`bg-white border rounded-xl p-4 ${source === 'flow' ? 'lg:col-span-3' : 'lg:col-span-1'}`}>
                    <h2 className="text-sm font-semibold text-gray-700 mb-3">Por código de erro</h2>
                    {overview.isLoading ? (
                        <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-violet-600" /></div>
                    ) : (
                        <div className="space-y-1.5">
                            {codeGroups.map((g) => {
                                const pct = Math.round((g.total / totalFail) * 100);
                                const active = (codeFilter === 'timeout' && g.code == null) || codeFilter === g.code;
                                return (
                                    <button
                                        key={g.label}
                                        onClick={() => setCodeFilter(active ? null : (g.code == null ? 'timeout' : g.code))}
                                        className={`w-full text-left rounded-lg p-2 transition-colors ${active ? 'bg-violet-50 ring-1 ring-violet-200' : 'hover:bg-gray-50'}`}
                                    >
                                        <div className="flex items-baseline justify-between gap-2">
                                            <span className="text-sm font-medium text-gray-800">{g.label}</span>
                                            <span className="text-sm tabular-nums text-gray-600 shrink-0">{fmt(g.total)} <span className="text-gray-400">({pct}%)</span></span>
                                        </div>
                                        <div className="text-[11px] text-gray-500 leading-snug">{g.hint}</div>
                                        <div className="h-1.5 bg-gray-100 rounded mt-1 overflow-hidden">
                                            <div className={`h-full rounded ${toneBar[g.tone]}`} style={{ width: `${Math.max(2, pct)}%` }} />
                                        </div>
                                    </button>
                                );
                            })}
                            {!codeGroups.length && <div className="text-sm text-gray-400 py-4 text-center">Sem falhas no período. 🎉</div>}
                        </div>
                    )}
                </section>

                {/* Por aplicativo (só broadcast — esteira/flow não têm app_id) */}
                {source === 'broadcast' && (
                <section className="bg-white border rounded-xl overflow-hidden lg:col-span-2 flex flex-col">
                    <div className="flex items-center justify-between gap-2 p-4 pb-2">
                        <h2 className="text-sm font-semibold text-gray-700">Por aplicativo</h2>
                        <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
                            <input type="checkbox" className="h-3.5 w-3.5" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
                            Apenas apps ativos
                        </label>
                    </div>
                    <div className="overflow-x-auto flex-1">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[11px] uppercase text-gray-500 border-b bg-gray-50">
                                    <th className="text-left font-medium px-4 py-2">Aplicativo</th>
                                    <SortTh label="Total" col="total" sort={appSort} onSort={onAppSort} />
                                    <SortTh label="Sucesso" col="sucesso" sort={appSort} onSort={onAppSort} />
                                    <SortTh label="% OK" col="pct_sucesso" sort={appSort} onSort={onAppSort} />
                                    <th className="text-left font-medium px-3 py-2">Principais erros</th>
                                </tr>
                            </thead>
                            <tbody>
                                {apps.map((a) => (
                                    <tr
                                        key={String(a.app_id)}
                                        className={`border-b last:border-0 hover:bg-gray-50 cursor-pointer ${appFilter === a.app_id ? 'bg-violet-50/50' : ''}`}
                                        onClick={() => { setAppFilter(appFilter === a.app_id ? null : a.app_id); setPage(1); }}
                                        title="Clique pra filtrar a tabela de páginas por esse app"
                                    >
                                        <td className="px-4 py-2.5">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-gray-800">{a.app_name}</span>
                                                {a.app_active === false && <Badge variant="outline" className="text-[9px] bg-gray-100 text-gray-500">inativo</Badge>}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{fmt(a.total)}</td>
                                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{fmt(a.sucesso)}</td>
                                        <td className={`px-3 py-2.5 text-right tabular-nums font-semibold ${pctColor(a.pct_sucesso)}`}>{a.pct_sucesso}%</td>
                                        <td className="px-3 py-2.5"><ErrorMix row={a} onPick={pickCode} /></td>
                                    </tr>
                                ))}
                                {overview.isLoading && <tr><td colSpan={5} className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-violet-600 inline" /></td></tr>}
                                {!overview.isLoading && !apps.length && <tr><td colSpan={5} className="py-6 text-center text-gray-400">Sem envios no período.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </section>
                )}
            </div>
            )}

            {/* Por página (todas as fontes) */}
            <section className="bg-white border rounded-xl overflow-hidden">
                <div className="p-4 pb-3 flex flex-wrap items-center gap-3 border-b">
                    <h2 className="text-sm font-semibold text-gray-700 shrink-0">Por página</h2>
                    <div className="relative flex-1 min-w-[200px] max-w-xs">
                        <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Buscar por nome ou page_id…"
                            className="w-full pl-8 pr-3 py-1.5 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400"
                        />
                    </div>
                    {source === 'broadcast' && (
                        <div className="w-52">
                            <SearchableSelect
                                value={appFilter != null ? String(appFilter) : ''}
                                onValueChange={(v) => { setAppFilter(v ? Number(v) : null); setPage(1); }}
                                options={appOptions}
                                placeholder="Todos os apps"
                                searchPlaceholder="Buscar app…"
                            />
                        </div>
                    )}
                    {source === 'broadcast' && codeFilter != null && (
                        <Badge variant="outline" className="text-[11px] gap-1">
                            {codeFilter === 'timeout' ? 'Timeout' : (BUCKETS.find((b) => b.code === codeFilter)?.label ?? `#${codeFilter}`)}
                            <button onClick={() => { setCodeFilter(null); setPage(1); }} className="hover:text-red-500"><X className="w-3 h-3" /></button>
                        </Badge>
                    )}
                    {hasFilter && <button onClick={clearFilters} className="text-xs text-violet-600 hover:underline ml-auto">limpar filtros</button>}
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[11px] uppercase text-gray-500 border-b bg-gray-50">
                                <th className="text-left font-medium px-4 py-2">Página</th>
                                {source === 'esteira' ? (
                                    <>
                                        <SortTh label="Enviados" col="sucesso" sort={pageSort} onSort={onPageSort} />
                                        <SortTh label="Falhas" col="falha" sort={pageSort} onSort={onPageSort} />
                                        <SortTh label="% OK" col="pct_sucesso" sort={pageSort} onSort={onPageSort} />
                                        <SortTh label="Leads" col="leads" sort={pageSort} onSort={onPageSort} />
                                    </>
                                ) : (
                                    <>
                                        <SortTh label="Total" col="total" sort={pageSort} onSort={onPageSort} />
                                        <SortTh label="Sucesso" col="sucesso" sort={pageSort} onSort={onPageSort} />
                                        <SortTh label="Entregues" col="entregues" sort={pageSort} onSort={onPageSort} />
                                        <SortTh label="% OK" col="pct_sucesso" sort={pageSort} onSort={onPageSort} />
                                        <th className="text-left font-medium px-3 py-2">Principais erros</th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {(byPage.data?.items || []).map((p) => (
                                <tr key={p.page_id} className="border-b last:border-0 hover:bg-gray-50">
                                    <td className="px-4 py-2.5">
                                        <div className="font-medium text-gray-800 truncate max-w-[260px]" title={p.page_name}>{p.page_name}</div>
                                        <div className="text-[11px] font-mono text-gray-400">{p.page_id}</div>
                                    </td>
                                    {source === 'esteira' ? (
                                        <>
                                            <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{fmt(p.sucesso)}</td>
                                            <td className="px-3 py-2.5 text-right tabular-nums text-red-600">{fmt(p.falha)}</td>
                                            <td className={`px-3 py-2.5 text-right tabular-nums font-semibold ${pctColor(p.pct_sucesso)}`}>{p.pct_sucesso}%</td>
                                            <td className="px-3 py-2.5 text-right tabular-nums text-gray-500">{fmt(p.leads ?? 0)}</td>
                                        </>
                                    ) : (
                                        <>
                                            <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{fmt(p.total)}</td>
                                            <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{fmt(p.sucesso)}</td>
                                            <td className="px-3 py-2.5 text-right tabular-nums text-emerald-600">{fmt(p.entregues)}</td>
                                            <td className={`px-3 py-2.5 text-right tabular-nums font-semibold ${pctColor(p.pct_sucesso)}`}>{p.pct_sucesso}%</td>
                                            <td className="px-3 py-2.5"><ErrorMix row={p} onPick={pickCode} /></td>
                                        </>
                                    )}
                                </tr>
                            ))}
                            {byPage.isLoading && <tr><td colSpan={pageCols} className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-violet-600 inline" /></td></tr>}
                            {!byPage.isLoading && !byPage.data?.items?.length && <tr><td colSpan={pageCols} className="py-6 text-center text-gray-400">Nenhuma página com envios nesse filtro.</td></tr>}
                        </tbody>
                    </table>
                </div>
                {byPage.data && byPage.data.total > byPage.data.per_page && (
                    <div className="flex items-center justify-between px-4 py-3 border-t text-sm text-gray-600">
                        <span className="tabular-nums">{fmt(byPage.data.total)} páginas · pág {page}/{totalPages}</span>
                        <div className="flex gap-1">
                            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="p-1.5 rounded border disabled:opacity-40 hover:bg-gray-50"><ChevronLeft className="w-4 h-4" /></button>
                            <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="p-1.5 rounded border disabled:opacity-40 hover:bg-gray-50"><ChevronRight className="w-4 h-4" /></button>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}
